import { Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { lastValueFrom } from 'rxjs';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { angularCommonImports } from 'src/app/shared/ui/angular-common';
import { convivaCommonImports } from 'src/app/shared/ui/conviva-common';
import { libsCommonImports } from 'src/app/shared/ui/libs-common';
import { ConvivaErrorHandlingService } from 'src/app/shared/services/convivaErrorHandlingService';
import { PlanoService } from 'src/app/shared/services/plano.service';
import { PlanoCadastro } from 'src/app/shared/models/plano/plano';

@Component({
  selector: 'app-cadastro-plano-periodo',
  templateUrl: './cadastro-plano-periodo.component.html',
  styleUrl: './cadastro-plano-periodo.component.scss',
  standalone: true,
  imports: [angularCommonImports, convivaCommonImports, libsCommonImports],
})
export class CadastroPlanoPeriodoComponent implements OnInit {
  private readonly spinnerService = inject(NgxSpinnerService);
  private readonly convivaErrors = inject(ConvivaErrorHandlingService);
  private readonly toastr = inject(ToastrService);
  private readonly planoService = inject(PlanoService);

  private readonly planoPadrao = 'PMCE - Envio de evidências';

  public formCadastroPlano!: FormGroup;
  public submitted = false;

  ngOnInit(): void {
    this.formCadastroPlano = new FormGroup({
      descricaoPlano: new FormControl(this.planoPadrao, [Validators.required, Validators.maxLength(150)])
    });
  }

  public async onCadastrar(): Promise<void> {
    this.submitted = true;
    this.markAllControlsAsTouched();

    const descricaoPlano = this.formCadastroPlano.get('descricaoPlano')?.value?.trim();

    if (!this.formCadastroPlano.valid || !descricaoPlano) {
      this.toastr.warning('Por favor, informe o plano antes de cadastrar.');
      this.scrollToFirstInvalidControl();
      return;
    }

    this.spinnerService.show();

    try {
      const plano = this.setPlanoCadastro(descricaoPlano);
      const response = await lastValueFrom(this.planoService.cadastrarPlano(plano));

      if (!response.isSucess) {
        this.toastr.error(response.message);
        return;
      }

      this.toastr.success(response.data?.mensagem ?? response.message ?? 'Plano cadastrado com sucesso!');
      this.formCadastroPlano.reset({ descricaoPlano: this.planoPadrao });
      this.submitted = false;
    } catch (err) {
      this.convivaErrors.handleResultError(err);
    } finally {
      this.spinnerService.hide();
    }
  }

  public onLimpar(): void {
    this.submitted = false;
    this.formCadastroPlano.reset({ descricaoPlano: this.planoPadrao });
    this.removeValidationStyles();
  }

  private setPlanoCadastro(descricaoPlano: string): PlanoCadastro {
    return {
      descricaoPlano
    };
  }

  private markAllControlsAsTouched(): void {
    Object.keys(this.formCadastroPlano.controls).forEach(key => {
      const control = this.formCadastroPlano.get(key);
      control?.markAsTouched();
    });
  }

  private scrollToFirstInvalidControl(): void {
    setTimeout(() => {
      const firstInvalidControl = document.querySelector('.is-invalid');
      if (firstInvalidControl) {
        firstInvalidControl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  }

  private removeValidationStyles(): void {
    const invalidElements = document.querySelectorAll('.is-invalid');
    invalidElements.forEach(element => {
      element.classList.remove('is-invalid');
    });
  }

  get descricaoPlano() { return this.formCadastroPlano.get('descricaoPlano'); }
}
